import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as HiIcons from 'react-icons/hi';
import Select from './Select';
import {
  getStatusInfo,
  getStatusIcon,
  getStatusColor
} from '../utils/statusConfig';
import apiService from '../services/api';
import { normalizeImageUrl, normalizeApiData } from '../utils/apiHelpers';
import Notification from './Notification';
import { useNotification } from '../hooks/useNotification';
import './Verify.css';

// Retorna o código de acesso direto de data.codigos_acesso (string ou primeiro disponível no objeto)
function getCodigoAcessoFromData(invoiceData) {
  if (!invoiceData) return '';
  let codigos = invoiceData.codigos_acesso;
  if (typeof codigos === 'string' && codigos.trim() !== '') {
    try {
      const parsed = JSON.parse(codigos);
      if (parsed && typeof parsed === 'object') codigos = parsed;
      else return codigos.trim();
    } catch (e) {
      return codigos.trim();
    }
  }
  if (!codigos || typeof codigos !== 'object') return '';
  return codigos.administrador || codigos.gerente || codigos.transportador || codigos.entregador || '';
}

const Verify = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const code = searchParams.get('code');

  // Função para exibir valor formatado
  const displayCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    if (typeof value === 'string' && value.includes('R$')) {
      return value;
    }
    const number = typeof value === 'string'
      ? parseFloat(value.replace(/[^\d,.-]/g, '').replace(',', '.')) || 0
      : value || 0;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(number);
  };
  const [boxData, setBoxData] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fullCode, setFullCode] = useState('');
  const [showFullInfo, setShowFullInfo] = useState(false);
  const [verifyingAccess, setVerifyingAccess] = useState(false);
  const [packages, setPackages] = useState([]); // Pacotes retornados do verifyAccess
  const [historicos, setHistoricos] = useState([]); // Históricos retornados do verifyAccess
  const [showFullHistory, setShowFullHistory] = useState(false); // Controla se o histórico completo está visível
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [withdrawalData, setWithdrawalData] = useState({
    numero_documento: '',
    codigo_acesso: '',
    codigo_cliente: ''
  });
  const [clientPackages, setClientPackages] = useState([]);
  const [clientInfo, setClientInfo] = useState(null); // Informações do cliente solicitante
  const [verifyingClient, setVerifyingClient] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState(null); // Mensagem de retorno da verificação
  const [verificationMessageType, setVerificationMessageType] = useState(null); // 'success', 'error', 'warning'
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [reportData, setReportData] = useState({
    tipo: '',
    descricao: '',
    fotos: []
  });
  const [reportingPackage, setReportingPackage] = useState(false);
  const [showViewReportsModal, setShowViewReportsModal] = useState(false);
  const [selectedPackageForReports, setSelectedPackageForReports] = useState(null);
  const [showWithdrawalForm, setShowWithdrawalForm] = useState(false);
  const [withdrawalFormData, setWithdrawalFormData] = useState({
    foto_saida: null,
    foto_assinatura: null,
    foto_documento: null
  });
  const [submittingWithdrawal, setSubmittingWithdrawal] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const signatureCanvasRef = useRef(null);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraField, setCameraField] = useState(null); // 'foto_saida' ou 'foto_documento' ou 'report_foto'
  const [cameraFacingMode, setCameraFacingMode] = useState('environment'); // 'environment' = traseira, 'user' = frontal
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const { notifications, removeNotification, showWarning, showError, showSuccess } = useNotification();

  // Função para comprimir imagem
  const compressImage = (file, maxWidth = 1920, maxHeight = 1920, quality = 0.6) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Redimensionar se necessário
          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            } else {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Converter para blob comprimido
          canvas.toBlob((blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, { type: 'image/jpeg' });
              resolve(compressedFile);
            } else {
              resolve(file);
            }
          }, 'image/jpeg', quality);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const [searchCode, setSearchCode] = useState('');
  const [invoicesDisponiveis, setInvoicesDisponiveis] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  useEffect(() => {
    if (code) {
      loadData(code);
    } else {
      setLoading(false);
    }
  }, [code]);

  // Preencher código de acesso automaticamente direto de data.codigos_acesso
  useEffect(() => {
    if (invoiceData && !showFullInfo) {
      const code = getCodigoAcessoFromData(invoiceData);
      setFullCode(code || '');
    }
  }, [invoiceData, showFullInfo]);

  // Carregar invoices com status "Disponível para retirada" para o select
  useEffect(() => {
    if (!code) {
      const loadInvoicesDisponiveis = async () => {
        try {
          setLoadingInvoices(true);
          const data = await apiService.getInvoicesList({
            status: 'disponivel_retirada',
            per_page: 500
          });
          const list = normalizeApiData(data);
          setInvoicesDisponiveis(list.map(inv => ({
            id: inv.id,
            codigo_rastreamento: inv.numero_documento || inv.numero || inv.codigo_rastreamento || inv.codigo_rastreio || inv.tracking_code || '',
            origem: inv.origem || inv.origin || '—',
            destino: inv.destino || inv.destination || '—'
          })).filter(inv => inv.codigo_rastreamento));
        } catch (err) {
          console.error('Erro ao carregar invoices disponíveis:', err);
          setInvoicesDisponiveis([]);
        } finally {
          setLoadingInvoices(false);
        }
      };
      loadInvoicesDisponiveis();
    }
  }, [code]);

  const handleSearchCode = async (e) => {
    e.preventDefault();
    if (!searchCode.trim()) {
      showError('Por favor, selecione um invoice para retirada');
      return;
    }
    // Atualizar a URL com o código pesquisado
    setSearchParams({ code: searchCode.trim() });
    setLoading(true);
    setError(null);
    await loadData(searchCode.trim());
  };

  const loadData = async (boxCode) => {
    try {
      const data = await apiService.verifyCode(boxCode);

      if (!data || !data.box) {
        setError('Caixa/Mala não encontrada');
        setLoading(false);
        return;
      }

      const box = data.box;

      // Tratar objetos complexos que não podem ser renderizados diretamente
      if (box.status_invoice && typeof box.status_invoice === 'object') {
        // Se status_invoice for um objeto, converter para string ou extrair o valor necessário
        box.status_invoice = box.status_invoice.nome || box.status_invoice.name || box.status_invoice.id || JSON.stringify(box.status_invoice);
      }

      // Inicializar histórico se não existir
      if (!box.historico_status) {
        box.historico_status = [{
          status: box.status || 'pedido_criado',
          data: new Date().toISOString(),
          observacao: 'Status inicial',
          alterado_por: 'sistema'
        }];
      }

      setBoxData(box);

      if (data.invoice) {
        // Tratar objetos complexos no invoice também
        const invoice = { ...data.invoice };
        if (invoice.status_invoice && typeof invoice.status_invoice === 'object') {
          invoice.status_invoice = invoice.status_invoice.nome || invoice.status_invoice.name || invoice.status_invoice.id || JSON.stringify(invoice.status_invoice);
        }
        setInvoiceData(invoice);
      }

      setLoading(false);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.message || 'Erro ao carregar dados');
      setLoading(false);
    }
  };

  const handleFullCodeSubmit = async (e) => {
    e.preventDefault();

    const codigoAcesso = fullCode.trim() || (invoiceData ? getCodigoAcessoFromData(invoiceData) : '');
    if (!codigoAcesso) {
      showError('Código de acesso não disponível nos dados deste pedido.');
      return;
    }

    // Verificar se temos o numero_documento (código de rastreamento da invoice)
    const numeroDocumento = invoiceData?.codigo_rastreamento || invoiceData?.numero_documento;

    if (!numeroDocumento) {
      showError('Número do documento não encontrado');
      return;
    }

    try {
      setVerifyingAccess(true);

      // Chamar o endpoint de verificação de acesso (código = data.codigos_acesso do invoice)
      const response = await apiService.verifyAccess({
        numero_documento: numeroDocumento,
        codigo_acesso: codigoAcesso,
      });

      // Se a resposta for bem-sucedida, atualizar os dados e listar todos os pacotes do invoice
      if (response && response.status === 'success') {
        // Extrair lista de pacotes do invoice: data.pacotes, pacotes no root ou data como array
        const pacotesRes = response.data?.pacotes ?? response.pacotes ?? (Array.isArray(response.data) ? response.data : []);
        setPackages(Array.isArray(pacotesRes) ? pacotesRes : []);

        // A resposta deve trazer os históricos de rastreamento
        if (response.data?.historicos) {
          setHistoricos(Array.isArray(response.data.historicos) ? response.data.historicos : []);
        }

        // Atualizar invoiceData com os dados retornados
        if (response.data) {
          setInvoiceData(prev => {
            const updated = {
              ...prev,
              ...response.data,
              numero_documento: response.data.numero_documento,
              codigo_rastreamento: response.data.codigo_rastreio,
              origem: response.data.origem,
              destino: response.data.destino,
              data_criacao: response.data.data_criacao,
              cliente_destinatario: response.data.cliente_destinatario,
              endereco_entrega: response.data.endereco_entrega,
              telefone_cliente: response.data.telefone_cliente
            };
            // Garantir que campos complexos sejam tratados corretamente
            if (updated.status_invoice && typeof updated.status_invoice === 'object') {
              updated.status_invoice = updated.status_invoice.nome || updated.status_invoice.name || JSON.stringify(updated.status_invoice);
            }
            return updated;
          });
        }

        // Se a resposta contiver dados atualizados da caixa/invoice, atualizar o estado
        if (response.data?.box) {
          setBoxData(prev => {
            const updated = { ...prev, ...response.data.box };
            // Garantir que campos complexos sejam tratados corretamente
            if (updated.status_invoice && typeof updated.status_invoice === 'object') {
              updated.status_invoice = updated.status_invoice.nome || updated.status_invoice.name || JSON.stringify(updated.status_invoice);
            }
            return updated;
          });
        }

        // Salvar código de acesso no localStorage
        if (codigoAcesso) {
          localStorage.setItem('lastAccessCode', codigoAcesso);
        }

        setShowFullInfo(true);
        showSuccess('Código de acesso verificado com sucesso!');
      } else {
        showError('Código de acesso inválido');
      }
    } catch (error) {
      console.error('Erro ao verificar código de acesso:', error);
      showError(error.message || 'Código de acesso inválido');
    } finally {
      setVerifyingAccess(false);
    }
  };

  const handleWithdrawalClick = () => {
    // Preencher numero_documento e código de acesso direto de data.codigos_acesso
    const numeroDoc = invoiceData?.codigo_rastreamento || invoiceData?.numero_documento || code || '';
    const codigoAcesso = getCodigoAcessoFromData(invoiceData);
    setWithdrawalData({
      numero_documento: numeroDoc,
      codigo_acesso: codigoAcesso || '',
      codigo_cliente: ''
    });
    setShowWithdrawalModal(true);
    setClientPackages([]);
    setShowWithdrawalForm(false);
  };

  const handleVerifyClient = async (e) => {
    e.preventDefault();

    if (!withdrawalData.codigo_cliente || !withdrawalData.codigo_acesso || !withdrawalData.numero_documento) {
      showWarning('Preencha todos os campos');
      return;
    }

    try {
      setVerifyingClient(true);
      const response = await apiService.verifyClientPackages({
        codigo_cliente: withdrawalData.codigo_cliente,
        codigo_acesso: withdrawalData.codigo_acesso,
        numero_documento: withdrawalData.numero_documento
      });

      if (response && response.status === 'success') {
        // Salvar código de acesso no localStorage
        if (withdrawalData.codigo_acesso && withdrawalData.codigo_acesso.trim()) {
          localStorage.setItem('lastAccessCode', withdrawalData.codigo_acesso.trim());
        }

        // A resposta já vem com pacotes e cliente no nível raiz
        // Estrutura: { status: "success", numero_documento: "...", cliente: {...}, pacotes: [...] }
        const pacotes = response.pacotes || response.data?.pacotes || [];
        const cliente = response.cliente || response.data?.cliente || {};
        const message = response.message || response.msg || '';

        setClientPackages(pacotes);
        // Armazenar informações do cliente solicitante
        setClientInfo({
          codigo: cliente.codigo || '',
          nome: cliente.nome || ''
        });

        // Exibir mensagem de retorno apenas se houver mensagem da API ou se não houver pacotes
        if (message) {
          setVerificationMessage(message);
          setVerificationMessageType('success');
        } else if (pacotes.length === 0) {
          setVerificationMessage('Nenhum pacote encontrado para este cliente.');
          setVerificationMessageType('warning');
        } else {
          // Não exibir mensagem quando pacotes são encontrados
          setVerificationMessage(null);
          setVerificationMessageType(null);
        }

        setShowWithdrawalForm(true);
      } else {
        const errorMsg = response?.message || response?.msg || 'Não foi possível encontrar pacotes para este cliente';
        setVerificationMessage(errorMsg);
        setVerificationMessageType('error');
        setClientPackages([]);
        setClientInfo(null);
        setShowWithdrawalForm(false);
        showError(errorMsg);
      }
    } catch (error) {
      console.error('Erro ao verificar cliente:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Erro ao verificar pacotes do cliente';
      setVerificationMessage(errorMsg);
      setVerificationMessageType('error');
      setClientPackages([]);
      setClientInfo(null);
      setShowWithdrawalForm(false);
      showError(errorMsg);
    } finally {
      setVerifyingClient(false);
    }
  };

  const handleReportPackage = (pkg) => {
    setSelectedPackage(pkg);
    setReportData({
      tipo: '',
      descricao: '',
      fotos: []
    });
    setShowReportModal(true);
  };

  const handleSubmitReport = async () => {
    if (!selectedPackage || !reportData.tipo) {
      showWarning('Selecione o tipo de ocorrência');
      return;
    }

    const numeroDoc = invoiceData?.codigo_rastreamento || invoiceData?.numero_documento;
    if (!numeroDoc) {
      showError('Número do documento não encontrado');
      return;
    }

    // Usar o código de acesso que foi usado para verificar (fullCode)
    const codigoAcesso = fullCode.trim();
    if (!codigoAcesso) {
      showError('Código de acesso não encontrado');
      return;
    }

    try {
      setReportingPackage(true);
      await apiService.reportPackageOccurrence({
        pacote_id: selectedPackage.id,
        numero_documento: numeroDoc,
        codigo_acesso: codigoAcesso,
        tipo: reportData.tipo,
        descricao: reportData.descricao || undefined,
        fotos: reportData.fotos && reportData.fotos.length > 0 ? reportData.fotos : undefined
      });

      showSuccess('Ocorrência reportada com sucesso!');
      setShowReportModal(false);
      setSelectedPackage(null);
      setReportData({ tipo: '', descricao: '', fotos: [] });
    } catch (error) {
      console.error('Erro ao reportar ocorrência:', error);
      showError(error.message || 'Erro ao reportar ocorrência');
    } finally {
      setReportingPackage(false);
    }
  };

  const handleFileChange = (field, file) => {
    if (file) {
      setWithdrawalFormData(prev => ({
        ...prev,
        [field]: file
      }));
    }
  };

  const removeFile = (field) => {
    setWithdrawalFormData(prev => ({
      ...prev,
      [field]: null
    }));
  };

  const getFilePreview = (file) => {
    if (!file) return null;
    return URL.createObjectURL(file);
  };

  // Funções para câmera
  const handleOpenCamera = (field) => {
    setCameraField(field);
    setShowCameraModal(true);
  };

  const handleCloseCamera = () => {
    setShowCameraModal(false);
    setCameraField(null);
    if (videoRef.current) {
      const stream = videoRef.current.srcObject;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      videoRef.current.srcObject = null;
    }
  };

  const startCamera = async () => {
    try {
      // Parar stream anterior se existir
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject;
        stream.getTracks().forEach(track => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: cameraFacingMode }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Erro ao acessar câmera:', error);
      showError('Não foi possível acessar a câmera. Verifique as permissões.');
      handleCloseCamera();
    }
  };

  const toggleCamera = async () => {
    setCameraFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
    // A câmera será reiniciada no useEffect quando cameraFacingMode mudar
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current || !cameraField) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Ajustar dimensões do canvas para o vídeo
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Desenhar frame do vídeo no canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Converter canvas para blob e depois para File
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `foto_${cameraField}_${Date.now()}.jpg`, { type: 'image/jpeg' });
        setWithdrawalFormData(prev => ({
          ...prev,
          [cameraField]: file
        }));
        showSuccess('Foto capturada com sucesso!');
        handleCloseCamera();
      }
    }, 'image/jpeg', 0.9);
  };

  useEffect(() => {
    if (showCameraModal && videoRef.current) {
      startCamera();
    }
    return () => {
      if (videoRef.current) {
        const stream = videoRef.current.srcObject;
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
      }
    };
  }, [showCameraModal, cameraFacingMode]);

  // Funções para assinatura digital
  const handleSignatureStart = () => {
    setShowSignatureModal(true);
  };

  const handleSignatureClose = () => {
    setShowSignatureModal(false);
    if (signatureCanvasRef.current) {
      const ctx = signatureCanvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, signatureCanvasRef.current.width, signatureCanvasRef.current.height);
    }
  };

  const handleSignatureClear = () => {
    if (signatureCanvasRef.current) {
      const ctx = signatureCanvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, signatureCanvasRef.current.width, signatureCanvasRef.current.height);
    }
  };

  const handleSignatureConfirm = () => {
    if (!signatureCanvasRef.current) return;

    // Converter canvas para blob e depois para File
    signatureCanvasRef.current.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], 'assinatura.png', { type: 'image/png' });
        setWithdrawalFormData(prev => ({
          ...prev,
          foto_assinatura: file
        }));
        setShowSignatureModal(false);
        showSuccess('Assinatura capturada com sucesso!');
      }
    }, 'image/png');
  };

  // Setup do canvas quando o modal abrir
  useEffect(() => {
    if (!showSignatureModal || !signatureCanvasRef.current) return;

    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    let lastX = 0;
    let lastY = 0;
    let drawing = false;

    const getCoordinates = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return {
        x: ((e.clientX || (e.touches && e.touches[0].clientX)) - rect.left) * scaleX,
        y: ((e.clientY || (e.touches && e.touches[0].clientY)) - rect.top) * scaleY
      };
    };

    const startDrawing = (e) => {
      e.preventDefault();
      drawing = true;
      const coords = getCoordinates(e);
      lastX = coords.x;
      lastY = coords.y;
    };

    const draw = (e) => {
      if (!drawing) return;
      e.preventDefault();

      const coords = getCoordinates(e);
      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();

      lastX = coords.x;
      lastY = coords.y;
    };

    const stopDrawing = () => {
      drawing = false;
    };

    // Eventos de mouse
    canvas.addEventListener('mousedown', startDrawing);
    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', stopDrawing);
    canvas.addEventListener('mouseout', stopDrawing);

    // Eventos de touch
    canvas.addEventListener('touchstart', startDrawing);
    canvas.addEventListener('touchmove', draw);
    canvas.addEventListener('touchend', stopDrawing);
    canvas.addEventListener('touchcancel', stopDrawing);

    return () => {
      canvas.removeEventListener('mousedown', startDrawing);
      canvas.removeEventListener('mousemove', draw);
      canvas.removeEventListener('mouseup', stopDrawing);
      canvas.removeEventListener('mouseout', stopDrawing);
      canvas.removeEventListener('touchstart', startDrawing);
      canvas.removeEventListener('touchmove', draw);
      canvas.removeEventListener('touchend', stopDrawing);
      canvas.removeEventListener('touchcancel', stopDrawing);
    };
  }, [showSignatureModal]);

  const handleSubmitWithdrawal = async () => {
    if (!withdrawalFormData.foto_saida || !withdrawalFormData.foto_documento) {
      showWarning('Por favor, anexe a foto da saída e a foto do documento');
      return;
    }

    if (!withdrawalData.codigo_cliente || !withdrawalData.codigo_acesso || !withdrawalData.numero_documento) {
      showError('Dados incompletos');
      return;
    }

    try {
      setSubmittingWithdrawal(true);
      await apiService.reportPackageWithdrawal({
        codigo_cliente: withdrawalData.codigo_cliente,
        numero_documento: withdrawalData.numero_documento,
        codigo_acesso: withdrawalData.codigo_acesso,
        foto_saida: withdrawalFormData.foto_saida,
        foto_assinatura: withdrawalFormData.foto_assinatura,
        foto_documento: withdrawalFormData.foto_documento
      });

      showSuccess('Retirada registrada com sucesso!');
      setShowWithdrawalModal(false);
      setWithdrawalData({ numero_documento: '', codigo_acesso: '', codigo_cliente: '' });
      setWithdrawalFormData({ foto_saida: null, foto_assinatura: null, foto_documento: null });
      setClientPackages([]);
      setClientInfo(null);
      setShowWithdrawalForm(false);
    } catch (error) {
      console.error('Erro ao registrar retirada:', error);
      showError(error.message || 'Erro ao registrar retirada');
    } finally {
      setSubmittingWithdrawal(false);
    }
  };


  const getStatusDisplayInfo = (status) => {
    const statusInfo = getStatusInfo(status);
    const iconName = getStatusIcon(status);
    const IconComponent = HiIcons[iconName] || HiIcons.HiClock;
    const color = getStatusColor(status);

    return {
      label: statusInfo.label,
      color,
      icon: IconComponent
    };
  };

  // Se não há código e não está carregando, mostrar campo de busca
  if (!code && !loading && !boxData && !error) {
    return (
      <div className="verify-container">
        <div className="verify-header">
          <div className="verify-logo">
            <img src="/SENDING_TO.png" alt="SendingTo Express" className="verify-logo-image" />
            <h1>SendingTo Express</h1>
          </div>
        </div>

        <div className="verify-content">
          <div className="verify-card">
            <h2>Rastreamento</h2>
            <form onSubmit={handleSearchCode} style={{ marginTop: '2rem' }}>
              <div className="form-group">
                <label>Buscar INVOICE (Remessa)</label>
                <select
                  value={searchCode}
                  onChange={(e) => setSearchCode(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    marginTop: '8px',
                    backgroundColor: '#fff',
                    cursor: 'pointer'
                  }}
                  disabled={loadingInvoices}
                >
                  <option value="">
                    {loadingInvoices
                      ? 'Carregando invoices...'
                      : invoicesDisponiveis.length === 0
                        ? 'Nenhum invoice disponível para retirada'
                        : 'Selecionar Remessa'}
                  </option>
                  {invoicesDisponiveis.map((inv) => (
                    <option key={inv.id} value={inv.codigo_rastreamento}>
                      {inv.codigo_rastreamento} — {inv.origem} → {inv.destino}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: '100%', marginTop: '1rem', padding: '12px' }}
                disabled={loading || loadingInvoices || invoicesDisponiveis.length === 0}
              >
                {loading ? 'Pesquisando...' : 'Pesquisar'}
              </button>
            </form>
            {error && (
              <div style={{ marginTop: '1rem', padding: '12px', background: '#fee', color: '#c33', borderRadius: '8px' }}>
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="verify-container">
        <div className="verify-loading">
          <div className="loading-spinner"></div>
          <p>Carregando informações...</p>
        </div>
      </div>
    );
  }

  if (error || !boxData) {
    return (
      <div className="verify-container">
        <div className="verify-header">
          <div className="verify-logo">
            <img src="/SENDING_TO.png" alt="SendingTo Express" className="verify-logo-image" />
            <h1>SendingTo Express</h1>
          </div>
        </div>
        <div className="verify-content">
          <div className="verify-card">
            <div className="verify-error">
              <HiIcons.HiXCircle size={48} color="#ef4444" />
              <h2>Erro</h2>
              <p>{error || 'Caixa/Mala não encontrada'}</p>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setError(null);
                  setBoxData(null);
                  setSearchCode('');
                }}
                style={{ marginTop: '1rem' }}
              >
                Nova Busca
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusDisplayInfo(boxData.status || 'pedido_criado');
  const StatusIcon = statusInfo.icon;

  return (
    <div className="verify-container">
      <div className="verify-header">
        <div className="verify-logo">
          <img src="/SENDING_TO.png" alt="SendingTo Express" className="verify-logo-image" />
          <h1>SendingTo Express</h1>
        </div>
      </div>

      <div className="verify-content">
        <div className="verify-card">
          <div className="verify-status">
            <div className="status-badge" style={{ backgroundColor: `${statusInfo.color}20`, color: statusInfo.color }}>
              <StatusIcon size={24} />
              <span>{statusInfo.label}</span>
            </div>
          </div>

          <div className="verify-section">
            <h2>Dados Básicos</h2>
            <div className="verify-info-grid">
              <div className="info-item">
                <strong>Código:</strong>
                <span>{boxData.codigo}</span>
              </div>
              <div className="info-item">
                <strong>Tipo:</strong>
                <span>{boxData.tipo === 'caixa' ? 'Caixa' : 'Mala'}</span>
              </div>
              <div className="info-item">
                <strong>Destinatário:</strong>
                <span>{boxData.cliente_nome}</span>
              </div>
              <div className="info-item">
                <strong>Peso:</strong>
                <span>{boxData.peso}</span>
              </div>
            </div>
          </div>

          <div className="verify-section">
            <h2>Origem e Destino</h2>
            <div className="verify-location-grid">
              <div className="location-item">
                <HiIcons.HiLocationMarker size={20} color="#17A2B8" />
                <div>
                  <strong>Origem:</strong>
                  <p>{invoiceData?.origem || 'Não informado'}</p>
                </div>
              </div>
                  <div className="location-item">
                    <HiIcons.HiLocationMarker size={20} color="#10b981" />
                <div>
                  <strong>Destino:</strong>
                  <p>{invoiceData?.destino || 'Não informado'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Botão Retirada - sempre visível */}
          <div className="verify-section verify-withdrawal-section">
            <button
              type="button"
              onClick={handleWithdrawalClick}
              className="withdrawal-btn"
            >
              <HiIcons.HiTruck size={24} />
              <span>Retirada</span>
            </button>
          </div>

          {!showFullInfo && (
            <div className="verify-section verify-code-section">
              <div className="code-access">
                <HiIcons.HiShieldCheck size={32} color="#6b7280" />
                <h3>Acesso Completo</h3>
                <p>Código de acesso preenchido automaticamente dos dados do pedido. Clique em Acessar para visualizar as informações completas e listar os pacotes.</p>
                <form onSubmit={handleFullCodeSubmit} className="code-form">
                  <input
                    type="hidden"
                    value={fullCode}
                    readOnly
                    aria-hidden="true"
                  />
                  <button
                    type="submit"
                    className="code-submit-btn"
                    disabled={verifyingAccess || !fullCode.trim()}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1.5rem',
                      fontSize: '1rem',
                      fontWeight: '600',
                      marginTop: '0.75rem'
                    }}
                  >
                    {verifyingAccess ? 'Verificando...' : 'Acessar'}
                  </button>
                </form>
              </div>
            </div>
          )}

          {showFullInfo && (
            <div className="verify-section verify-full-info">
                <h2>Informações Completas</h2>

              <div className="verify-info-grid">
                <div className="info-item">
                  <strong>Dimensões:</strong>
                  <span>
                    {boxData.dimensoes?.comprimento} x {boxData.dimensoes?.largura} x {boxData.dimensoes?.altura}
                  </span>
                </div>
                <div className="info-item">
                  <strong>Peso:</strong>
                  <span>{boxData.peso}</span>
                </div>
                <div className="info-item">
                  <strong>Valor da Remessa:</strong>
                  <span>{displayCurrency(boxData.valor_remessa)}</span>
                </div>
                <div className="info-item">
                  <strong>Conteúdo:</strong>
                  <span>{boxData.conteudo}</span>
                </div>
              </div>

              <div className="verify-history-section" style={{ marginTop: '2rem' }}>
                <h3 style={{ marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '600', color: '#1f2937' }}>
                  Histórico de Atualizações de Status
                </h3>
                {(historicos && historicos.length > 0) || (boxData.historico_status && boxData.historico_status.length > 0) ? (
                  <>
                    <div className="history-list" style={{ position: 'relative', paddingLeft: '2rem' }}>
                      {/* Linha vertical conectando os eventos */}
                      <div style={{
                        position: 'absolute',
                        left: '15px',
                        top: '0',
                        bottom: '0',
                        width: '2px',
                        background: 'linear-gradient(to bottom, #FF6B35, #FF8C42, #17A2B8)',
                        borderRadius: '2px'
                      }} />

                      {(() => {
                        const historyArray = (historicos && historicos.length > 0 ? historicos : boxData.historico_status || [])
                          .slice()
                          .reverse();
                        const displayHistory = showFullHistory ? historyArray : historyArray.slice(0, 1);

                        return displayHistory.map((item, index) => {
                        const status = item.status || item.status_novo;
                        const statusInfo = getStatusDisplayInfo(status);
                        const StatusIcon = statusInfo.icon;
                        const eventoEm = item.evento_em || item.data || item.created_at;

                        return (
                          <div
                            key={item.id || index}
                            className="history-item"
                            style={{
                              position: 'relative',
                              marginBottom: '2rem',
                              paddingLeft: '2rem',
                              animation: 'fadeInUp 0.5s ease-out',
                              animationDelay: `${index * 0.1}s`,
                              animationFillMode: 'both'
                            }}
                          >
                            {/* Marcador circular */}
                            <div
                              className="history-marker"
                              style={{
                                position: 'absolute',
                                left: '-1.5rem',
                                top: '0.5rem',
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                backgroundColor: statusInfo.color,
                                border: '4px solid #ffffff',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 2,
                                transition: 'transform 0.2s ease',
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                              <StatusIcon size={16} color="#ffffff" />
                            </div>

                            {/* Conteúdo do histórico */}
                            <div
                              className="history-content"
                              style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '12px',
                                padding: '1.25rem',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                border: '1px solid #e5e7eb',
                                transition: 'all 0.2s ease',
                                borderLeft: `4px solid ${statusInfo.color}`,
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)';
                                e.currentTarget.style.transform = 'translateX(4px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                                e.currentTarget.style.transform = 'translateX(0)';
                              }}
                            >
                              <div
                                className="history-header"
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  marginBottom: '0.75rem',
                                  flexWrap: 'wrap',
                                  gap: '0.5rem'
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                  <span
                                    style={{
                                      padding: '0.375rem 0.75rem',
                                      borderRadius: '6px',
                                      backgroundColor: `${statusInfo.color}15`,
                                      color: statusInfo.color,
                                      fontSize: '0.875rem',
                                      fontWeight: '600',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '0.5rem'
                                    }}
                                  >
                                    <StatusIcon size={14} />
                                    {statusInfo.label}
                                  </span>
                                </div>
                                {eventoEm && (
                                  <span
                                    className="history-date"
                                    style={{
                                      fontSize: '0.875rem',
                                      color: '#6b7280',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.5rem',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    <HiIcons.HiClock size={14} />
                                    {new Date(eventoEm).toLocaleString('pt-BR', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                )}
                              </div>

                              {(item.descricao || item.observacao) && (
                                <p
                                  className="history-observation"
                                  style={{
                                    marginTop: '0.5rem',
                                    marginBottom: '0.5rem',
                                    color: '#374151',
                                    fontSize: '0.9375rem',
                                    lineHeight: '1.5'
                                  }}
                                >
                                  {item.descricao || item.observacao}
                                </p>
                              )}

                              {(item.local_pacote || item.localizacao) && (
                                <div
                                  style={{
                                    marginTop: '0.75rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    color: '#6b7280',
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  <HiIcons.HiLocationMarker size={16} color="#17A2B8" />
                                  <span>{item.local_pacote || item.localizacao}</span>
                                </div>
                              )}

                              {item.alterado_por && (
                                <p
                                  className="history-user"
                                  style={{
                                    marginTop: '0.5rem',
                                    fontSize: '0.75rem',
                                    color: '#9ca3af'
                                  }}
                                >
                                  Alterado por: {item.alterado_por}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      });
                      })()}
                    </div>

                    {/* Botão para mostrar mais/menos */}
                    {(() => {
                      const totalHistory = (historicos && historicos.length > 0 ? historicos : boxData.historico_status || []);
                      return totalHistory.length > 1 && (
                        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                          <button
                            type="button"
                            onClick={() => setShowFullHistory(!showFullHistory)}
                            style={{
                              padding: '0.75rem 1.5rem',
                              backgroundColor: '#17A2B8',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#3a7bc8';
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(23, 162, 184, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#17A2B8';
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            {showFullHistory ? (
                              <>
                                <HiIcons.HiChevronUp size={18} />
                                Mostrar Menos
                              </>
                            ) : (
                              <>
                                <HiIcons.HiChevronDown size={18} />
                                Mostrar Mais ({totalHistory.length - 1} {totalHistory.length - 1 === 1 ? 'item' : 'itens'})
                              </>
                            )}
                          </button>
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <div
                    className="no-history"
                    style={{
                      textAlign: 'center',
                      padding: '3rem 2rem',
                      backgroundColor: '#f9fafb',
                      borderRadius: '12px',
                      border: '2px dashed #e5e7eb'
                    }}
                  >
                    <HiIcons.HiClock size={48} style={{ marginBottom: '1rem', opacity: 0.3, color: '#9ca3af' }} />
                    <p style={{ color: '#6b7280', fontSize: '1rem' }}>Nenhuma atualização registrada ainda.</p>
                  </div>
                )}
              </div>

              {/* Listar todos os pacotes do invoice (código de acesso de data.codigos_acesso) */}
              <div className="verify-section verify-packages-section">
                <h3>Pacotes do invoice ({packages?.length ?? 0})</h3>
                {packages && packages.length > 0 ? (
                  <div className="packages-list">
                    {packages.map((pkg, index) => (
                      <div key={pkg.id || index} className="package-card">
                        <div className="package-header">
                          <h4>Pacote {pkg.codigo_pacote || pkg.codigo || pkg.id || index + 1}</h4>
                          {pkg.status && (
                            <span className="package-status">
                              {(() => {
                                // Converter status para nome legível
                                const statusMap = {
                                  'em_consolidacao': 'Em Consolidação',
                                  'pedido_criado': 'Pedido Criado',
                                  'coletado': 'Coletado',
                                  'recebido_deposito': 'Recebido no Depósito',
                                  'em_transito': 'Em Trânsito',
                                  'em_transito_internacional': 'Em Trânsito Internacional',
                                  'em_transporte_local': 'Em Transporte Local',
                                  'chegou': 'Chegou',
                                  'chegada_destino': 'Chegou ao Destino',
                                  'disponivel_retirada': 'Disponível para Retirada',
                                  'entregue': 'Entregue',
                                  'entrega_confirmada': 'Entrega Confirmada',
                                  'extraviado': 'Extraviado',
                                  'violado': 'Violado',
                                  'retirado': 'Retirado',
                                  'retira_inspecao': 'Retira para Inspeção',
                                  'aguardando_documentacao_complementar': 'Aguardando Documentação Complementar',
                                  'taxacao_aplicada': 'Taxação Aplicada',
                                  'destinatario_ausente': 'Destinatário Ausente',
                                  'carga_avariada': 'Carga Avariada',
                                  'devolucao_remetente': 'Devolução ao Remetente',
                                  'entrega_cancelada': 'Entrega Cancelada',
                                  'pedido_cancelado': 'Pedido Cancelado'
                                };
                                // Se não encontrar no map, usar getStatusInfo para obter o label
                                if (statusMap[pkg.status]) {
                                  return statusMap[pkg.status];
                                }
                                const statusInfo = getStatusInfo(pkg.status);
                                return statusInfo.label || pkg.status;
                              })()}
                            </span>
                          )}
                        </div>
                        <div className="package-info">
                          {pkg.cliente?.nome && (
                            <div className="info-item">
                              <strong>Destinatário:</strong>
                              <span>{pkg.cliente.nome}</span>
                            </div>
                          )}
                          {pkg.peso_bruto && (
                            <div className="info-item">
                              <strong>Peso Bruto:</strong>
                              <span>{pkg.peso_bruto} kg</span>
                            </div>
                          )}
                          {pkg.peso_liquido && (
                            <div className="info-item">
                              <strong>Peso Líquido:</strong>
                              <span>{pkg.peso_liquido} kg</span>
                            </div>
                          )}
                          {pkg.conteudo && (
                            <div className="info-item">
                              <strong>Conteúdo:</strong>
                              <span>{pkg.conteudo}</span>
                            </div>
                          )}
                          {pkg.origem && (
                            <div className="info-item">
                              <strong>Origem:</strong>
                              <span>{pkg.origem}</span>
                            </div>
                          )}
                          {pkg.destino && (
                            <div className="info-item">
                              <strong>Destino:</strong>
                              <span>{pkg.destino}</span>
                            </div>
                          )}
                        </div>
                        {pkg.fotos && Array.isArray(pkg.fotos) && pkg.fotos.length > 0 && (
                          <div className="package-photos">
                            <strong>Fotos:</strong>
                            <div className="photos-grid">
                              {pkg.fotos.map((foto, fotoIndex) => {
                                const rawFotoUrl = typeof foto === 'string'
                                  ? foto
                                  : (foto?.url || foto?.preview || foto);
                                if (!rawFotoUrl) return null;

                                // Normalizar URL da imagem (remover barras invertidas e adicionar public/ se necessário)
                                const fotoUrl = normalizeImageUrl(rawFotoUrl);

                                return (
                                  <div key={fotoIndex} className="photo-item">
                                    <img
                                      src={fotoUrl}
                                      alt={`Foto ${fotoIndex + 1} do pacote`}
                                      onClick={() => window.open(fotoUrl, '_blank')}
                                      style={{ cursor: 'pointer', maxWidth: '100px', maxHeight: '100px', objectFit: 'cover' }}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        <div className="package-actions" style={{ display: 'flex', gap: '8px' }}>
                          {pkg.reportes && Array.isArray(pkg.reportes) && pkg.reportes.length > 0 && (
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => {
                                setSelectedPackageForReports(pkg);
                                setShowViewReportsModal(true);
                              }}
                              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                              <HiIcons.HiEye size={18} />
                              Ver Reportes ({pkg.reportes.length})
                            </button>
                          )}
                          {pkg.status !== 'entregue' && (
                            <button
                              type="button"
                              className="btn-report"
                              onClick={() => handleReportPackage(pkg)}
                            >
                              <HiIcons.HiExclamationCircle size={18} />
                              Reportar
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>Nenhum pacote encontrado para este invoice.</p>
                )}
              </div>

            </div>
          )}
        </div>
      </div>

      <div className="verify-footer">
        <p>© {new Date().getFullYear()} SendingTo Express. Todos os direitos reservados.</p>
      </div>

      {/* Modal de Retirada - não fecha ao clicar fora */}
      {showWithdrawalModal && (
        <div className="modal-overlay modal-withdrawal">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-header-title-with-icon">
                <HiIcons.HiTruck size={24} />
                Retirada de Pacote
              </h3>
              <button
                className="btn-icon"
                onClick={() => {
                  setShowWithdrawalModal(false);
                  setWithdrawalData({ numero_documento: '', codigo_acesso: '', codigo_cliente: '' });
                  setClientPackages([]);
                  setClientInfo(null);
                  setShowWithdrawalForm(false);
                  setVerificationMessage(null);
                  setVerificationMessageType(null);
                }}
              >
                <HiIcons.HiX size={20} />
              </button>
            </div>
            {!showWithdrawalForm ? (
              <div className="modal-body">
                <form onSubmit={handleVerifyClient} className="withdrawal-verify-form">
                  <div className="withdrawal-verify-form-inner">
                    <div className="withdrawal-verify-icon-wrap">
                      <HiIcons.HiIdentification size={48} className="withdrawal-verify-icon" />
                    </div>
                    <h4 className="withdrawal-verify-title">Verificar Cliente para Retirada</h4>
                    <p className="withdrawal-verify-desc">Informe o código do cliente que realizará a retirada.</p>
                    <div className="form-group form-group-centered">
                      <label htmlFor="withdrawal-codigo-cliente">
                        <HiIcons.HiUser size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                        Código do Cliente
                      </label>
                      <input
                        id="withdrawal-codigo-cliente"
                        type="text"
                        value={withdrawalData.codigo_cliente}
                        onChange={(e) => setWithdrawalData(prev => ({ ...prev, codigo_cliente: e.target.value }))}
                        placeholder="Digite o código do cliente"
                        required
                        className="withdrawal-verify-input"
                        autoFocus
                      />
                    </div>
                    <button
                      type="submit"
                      className="btn btn-primary withdrawal-verify-btn"
                      disabled={verifyingClient}
                    >
                      {verifyingClient ? (
                        <>
                          <HiIcons.HiRefresh size={20} className="btn-icon-spin" />
                          Verificando...
                        </>
                      ) : (
                        <>
                          <HiIcons.HiSearch size={20} />
                          Verificar Cliente
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
                <div className="modal-withdrawal-body-footer-wrap">
                  <div className="modal-body">
                  {/* Mensagem de retorno da verificação */}
                  {verificationMessage && (
                    <div
                      style={{
                        padding: '1rem',
                        borderRadius: '12px',
                        marginBottom: '1.5rem',
                        backgroundColor: verificationMessageType === 'success'
                          ? '#d1fae5'
                          : verificationMessageType === 'error'
                          ? '#fee2e2'
                          : '#fef3c7',
                        border: `1px solid ${
                          verificationMessageType === 'success'
                            ? '#10b981'
                            : verificationMessageType === 'error'
                            ? '#ef4444'
                            : '#f59e0b'
                        }`,
                        color: verificationMessageType === 'success'
                          ? '#065f46'
                          : verificationMessageType === 'error'
                          ? '#991b1b'
                          : '#92400e',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem'
                      }}
                    >
                      {verificationMessageType === 'success' && <HiIcons.HiCheckCircle size={24} />}
                      {verificationMessageType === 'error' && <HiIcons.HiXCircle size={24} />}
                      {verificationMessageType === 'warning' && <HiIcons.HiExclamationCircle size={24} />}
                      <p style={{ margin: 0, fontWeight: '500' }}>{verificationMessage}</p>
                    </div>
                  )}

                  {/* Mensagem quando não encontrou pacotes */}
                  {clientPackages.length === 0 && !verifyingClient && verificationMessageType !== 'error' && (
                    <div
                      style={{
                        padding: '2rem',
                        textAlign: 'center',
                        backgroundColor: '#f9fafb',
                        borderRadius: '12px',
                        border: '2px dashed #d1d5db',
                        marginBottom: '1.5rem'
                      }}
                    >
                      <HiIcons.HiSearch size={48} style={{ color: '#9ca3af', marginBottom: '1rem' }} />
                      <h3 style={{ color: '#374151', marginBottom: '0.5rem' }}>Nenhum pacote encontrado</h3>
                      <p style={{ color: '#6b7280', margin: 0 }}>
                        Não foram encontrados pacotes para os dados informados. Verifique se o código do cliente, código de acesso e número do documento estão corretos.
                      </p>
                    </div>
                  )}

                  {/* Informações do Cliente Solicitante */}
                  {clientInfo && clientInfo.nome && (
                    <div className="client-info" style={{
                      backgroundColor: '#f9fafb',
                      padding: '1.25rem',
                      borderRadius: '12px',
                      marginBottom: '1.5rem',
                      border: '1px solid #e5e7eb'
                    }}>
                      <h4 style={{ marginBottom: '1rem', color: '#1f2937', fontSize: '1.125rem', fontWeight: '600' }}>
                        Cliente Solicitante
                      </h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
                        {clientInfo.codigo && (
                          <div>
                            <strong style={{ color: '#6b7280', fontSize: '0.875rem' }}>Código:</strong>
                            <p style={{ margin: '0.25rem 0 0 0', color: '#1f2937', fontWeight: '500' }}>{clientInfo.codigo}</p>
                          </div>
                        )}
                        {clientInfo.nome && (
                          <div>
                            <strong style={{ color: '#6b7280', fontSize: '0.875rem' }}>Nome:</strong>
                            <p style={{ margin: '0.25rem 0 0 0', color: '#1f2937', fontWeight: '500' }}>{clientInfo.nome}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Lista de Pacotes */}
                  {clientPackages.length > 0 && (
                    <div className="client-packages" style={{ marginBottom: '1.5rem' }}>
                      <h4 style={{ marginBottom: '1rem', color: '#1f2937', fontSize: '1.125rem', fontWeight: '600' }}>
                        Pacotes Encontrados ({clientPackages.length})
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {clientPackages.map((pkg, idx) => {
                          const statusInfo = getStatusInfo(pkg.status);
                          const statusLabel = statusInfo?.label || pkg.status;
                          const statusColor = getStatusColor(pkg.status) || '#6b7280';

                          // Verificar se o pacote já foi entregue
                          const isDelivered = pkg.status === 'entregue';

                          return (
                            <div
                              key={pkg.id || idx}
                              className="client-package-item"
                              style={{
                                backgroundColor: isDelivered ? '#f0fdf4' : '#ffffff',
                                border: isDelivered ? '2px solid #10b981' : '1px solid #e5e7eb',
                                borderRadius: '12px',
                                padding: '1.25rem',
                                boxShadow: isDelivered ? '0 4px 12px rgba(16, 185, 129, 0.15)' : '0 2px 4px rgba(0,0,0,0.05)',
                                position: 'relative',
                                overflow: 'hidden'
                              }}
                            >
                              {isDelivered && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    backgroundColor: '#10b981',
                                    color: 'white',
                                    padding: '0.5rem 1rem',
                                    borderBottomLeftRadius: '12px',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                  }}
                                >
                                  <HiIcons.HiCheckCircle size={18} />
                                  JÁ ENTREGUE
                                </div>
                              )}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                                <div>
                                  <h5 style={{ margin: '0 0 0.5rem 0', color: '#1f2937', fontSize: '1rem', fontWeight: '600' }}>
                                    Nº Pacote: {pkg.codigo_pacote || pkg.codigo || pkg.id}
                                  </h5>
                                  <span
                                    style={{
                                      padding: '0.25rem 0.75rem',
                                      borderRadius: '6px',
                                      backgroundColor: `${statusColor}15`,
                                      color: statusColor,
                                      fontSize: '0.875rem',
                                      fontWeight: '500',
                                      display: 'inline-block'
                                    }}
                                  >
                                    {statusLabel}
                                  </span>
                                </div>
                              </div>

                              {/* Informações principais do pacote */}
                              {/* Destaque para Nº Pacote, Nome Destinatário e Telefone Destinatário */}
                              <div className="package-highlight-box" style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '1rem',
                                marginBottom: '1.5rem',
                                marginLeft: '-1.5rem',
                                marginRight: '-1.5rem',
                                padding: '1.25rem 1.5rem',
                                backgroundColor: '#eff6ff',
                                borderRadius: '0',
                                borderLeft: 'none',
                                borderRight: 'none',
                                borderTop: '2px solid #17A2B8',
                                borderBottom: '2px solid #17A2B8',
                                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)',
                                width: 'calc(100% + 3rem)'
                              }}>
                                <div style={{
                                  wordBreak: 'break-word',
                                  overflowWrap: 'break-word',
                                  minWidth: 0,
                                  maxWidth: '100%'
                                }}>
                                  <strong style={{ color: '#1e40af', fontSize: '0.875rem', display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Nº Pacote:</strong>
                                  <span style={{
                                    color: '#1e3a8a',
                                    fontWeight: '700',
                                    fontSize: '1.25rem',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                    display: 'block',
                                    maxWidth: '100%'
                                  }}>{pkg.codigo_pacote || pkg.codigo || pkg.id || 'N/A'}</span>
                                </div>
                                <div style={{
                                  wordBreak: 'break-word',
                                  overflowWrap: 'break-word',
                                  minWidth: 0,
                                  maxWidth: '100%'
                                }}>
                                  <strong style={{ color: '#1e40af', fontSize: '0.875rem', display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Nome Destinatário:</strong>
                                  <span style={{
                                    color: '#1e3a8a',
                                    fontWeight: '700',
                                    fontSize: '1.125rem',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                    display: 'block',
                                    maxWidth: '100%'
                                  }}>{pkg.nome_destinatario || 'N/A'}</span>
                                </div>
                                <div style={{
                                  wordBreak: 'break-word',
                                  overflowWrap: 'break-word',
                                  minWidth: 0,
                                  maxWidth: '100%'
                                }}>
                                  <strong style={{ color: '#1e40af', fontSize: '0.875rem', display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Telefone Destinatário:</strong>
                                  <span style={{
                                    color: '#1e3a8a',
                                    fontWeight: '600',
                                    fontSize: '1rem',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                    display: 'block',
                                    maxWidth: '100%'
                                  }}>{pkg.telefone_destinatario || 'N/A'}</span>
                                </div>
                              </div>

                              {/* Conteúdo do pacote */}
                              <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                                <strong style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginBottom: '0.5rem' }}>Conteúdo:</strong>
                                <p style={{ margin: 0, color: '#1f2937', fontWeight: '500' }}>{pkg.conteudo || 'N/A'}</p>
                              </div>

                              {/* Informações adicionais */}
                              {(pkg.peso_bruto || pkg.peso_liquido || pkg.origem || pkg.destino) && (
                                <div style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                  gap: '0.75rem',
                                  marginBottom: '1rem'
                                }}>
                                  {pkg.peso_bruto && (
                                    <div>
                                      <strong style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Peso Bruto:</strong>
                                      <span style={{ color: '#1f2937' }}>{pkg.peso_bruto} kg</span>
                                    </div>
                                  )}
                                  {pkg.peso_liquido && (
                                    <div>
                                      <strong style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Peso Líquido:</strong>
                                      <span style={{ color: '#1f2937' }}>{pkg.peso_liquido} kg</span>
                                    </div>
                                  )}
                                  {pkg.origem && (
                                    <div>
                                      <strong style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Origem:</strong>
                                      <span style={{ color: '#1f2937' }}>{pkg.origem}</span>
                                    </div>
                                  )}
                                  {pkg.destino && (
                                    <div>
                                      <strong style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Destino:</strong>
                                      <span style={{ color: '#1f2937' }}>{pkg.destino}</span>
                                    </div>
                                  )}
                                </div>
                              )}

                              {pkg.fotos && Array.isArray(pkg.fotos) && pkg.fotos.length > 0 && (
                                <div style={{ marginTop: '1rem' }}>
                                  <strong style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginBottom: '0.75rem' }}>
                                    Fotos ({pkg.fotos.length}):
                                  </strong>
                                  <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                                    gap: '0.75rem'
                                  }}>
                                    {pkg.fotos.map((foto, fotoIdx) => {
                                      const rawFotoUrl = typeof foto === 'string' ? foto : (foto?.url || foto?.preview);
                                      if (!rawFotoUrl) return null;
                                      const fotoUrl = normalizeImageUrl(rawFotoUrl);

                                      return (
                                        <div
                                          key={fotoIdx}
                                          style={{
                                            position: 'relative',
                                            width: '100%',
                                            paddingBottom: '100%',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            border: '1px solid #e5e7eb',
                                            cursor: 'pointer',
                                            transition: 'transform 0.2s ease',
                                          }}
                                          onClick={() => window.open(fotoUrl, '_blank')}
                                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                        >
                                          <img
                                            src={fotoUrl}
                                            alt={`Foto ${fotoIdx + 1} do pacote ${pkg.codigo_pacote}`}
                                            style={{
                                              position: 'absolute',
                                              top: 0,
                                              left: 0,
                                              width: '100%',
                                              height: '100%',
                                              objectFit: 'cover'
                                            }}
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                              e.target.parentElement.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #999; font-size: 0.75rem;">Erro ao carregar</div>';
                                            }}
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {clientPackages.length > 0 && clientPackages.every(pkg => {
                    const pkgStatus = pkg.status?.toLowerCase() || '';
                    return pkgStatus !== 'retirado';
                  }) && (
                    <form id="withdrawal-form" onSubmit={(e) => { e.preventDefault(); handleSubmitWithdrawal(); }}>
                      <div className="upload-section">
                        <h4>Fotos Obrigatórias</h4>

                      {/* Foto da Saída */}
                      <div className="upload-group">
                        <label className="upload-label">Foto da Saída *</label>
                        <div className="upload-container">
                          {withdrawalFormData.foto_saida ? (
                            <div className="upload-preview">
                              <img src={getFilePreview(withdrawalFormData.foto_saida)} alt="Preview" />
                              <div className="upload-actions">
                                <button
                                  type="button"
                                  className="btn-remove"
                                  onClick={() => removeFile('foto_saida')}
                                >
                                  <HiIcons.HiX size={18} />
                                  Remover
                                </button>
                                <label className="btn-change">
                                  <HiIcons.HiPencil size={18} />
                                  Trocar
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleFileChange('foto_saida', e.target.files?.[0])}
                                    style={{ display: 'none' }}
                                  />
                                </label>
                              </div>
                            </div>
                          ) : (
                            <div className="photo-options">
                              <button
                                type="button"
                                className="btn-camera"
                                onClick={() => handleOpenCamera('foto_saida')}
                              >
                                <HiIcons.HiCamera size={24} />
                                Tirar Foto
                              </button>
                              <span className="photo-divider">ou</span>
                              <label className="btn-select-file">
                                <HiIcons.HiCloudUpload size={24} />
                                Selecionar no Dispositivo
                                <input
                                  type="file"
                                  accept="image/*"
                                  capture="environment"
                                  onChange={(e) => handleFileChange('foto_saida', e.target.files?.[0])}
                                  style={{ display: 'none' }}
                                />
                              </label>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Foto do Documento */}
                      <div className="upload-group">
                        <label className="upload-label">Foto do Documento *</label>
                        <div className="upload-container">
                          {withdrawalFormData.foto_documento ? (
                            <div className="upload-preview">
                              <img src={getFilePreview(withdrawalFormData.foto_documento)} alt="Preview" />
                              <div className="upload-actions">
                                <button
                                  type="button"
                                  className="btn-remove"
                                  onClick={() => removeFile('foto_documento')}
                                >
                                  <HiIcons.HiX size={18} />
                                  Remover
                                </button>
                                <label className="btn-change">
                                  <HiIcons.HiPencil size={18} />
                                  Trocar
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleFileChange('foto_documento', e.target.files?.[0])}
                                    style={{ display: 'none' }}
                                  />
                                </label>
                              </div>
                            </div>
                          ) : (
                            <div className="photo-options">
                              <button
                                type="button"
                                className="btn-camera"
                                onClick={() => handleOpenCamera('foto_documento')}
                              >
                                <HiIcons.HiCamera size={24} />
                                Tirar Foto
                              </button>
                              <span className="photo-divider">ou</span>
                              <label className="btn-select-file">
                                <HiIcons.HiCloudUpload size={24} />
                                Selecionar no Dispositivo
                                <input
                                  type="file"
                                  accept="image/*"
                                  capture="environment"
                                  onChange={(e) => handleFileChange('foto_documento', e.target.files?.[0])}
                                  style={{ display: 'none' }}
                                />
                              </label>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Registrar Assinatura */}
                      <div className="upload-group">
                        <label className="upload-label">Registrar Assinatura (opcional)</label>
                        <div className="upload-container">
                          {withdrawalFormData.foto_assinatura ? (
                            <div className="upload-preview">
                              <img src={getFilePreview(withdrawalFormData.foto_assinatura)} alt="Preview" />
                              <div className="upload-actions">
                                <button
                                  type="button"
                                  className="btn-remove"
                                  onClick={() => removeFile('foto_assinatura')}
                                >
                                  <HiIcons.HiX size={18} />
                                  Remover
                                </button>
                                <button
                                  type="button"
                                  className="btn-change"
                                  onClick={handleSignatureStart}
                                >
                                  <HiIcons.HiPencil size={18} />
                                  Assinar Novamente
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="signature-options" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                              <button
                                type="button"
                                className="btn-signature"
                                onClick={handleSignatureStart}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '12px 24px',
                                  backgroundColor: '#17A2B8',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontWeight: '500'
                                }}
                              >
                                <HiIcons.HiPencil size={24} />
                                Assinar Digitalmente
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </form>
                  )}

                  </div>
                  {/* Rodapé fixo com botão Registrar Retirada - só quando há pacotes para retirar */}
                  {clientPackages.length > 0 && clientPackages.every(pkg => {
                    const pkgStatus = pkg.status?.toLowerCase() || '';
                    return pkgStatus !== 'retirado';
                  }) && (
                    <div className="modal-footer-withdrawal">
                      <button
                        type="submit"
                        form="withdrawal-form"
                        className="btn btn-primary"
                        disabled={submittingWithdrawal}
                      >
                        {submittingWithdrawal ? 'Registrando...' : 'Registrar Retirada'}
                      </button>
                    </div>
                  )}
                </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de Câmera */}
      {showCameraModal && (
        <div className="modal-overlay" onClick={handleCloseCamera}>
          <div className="modal-content camera-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Tirar Foto</h3>
              <button
                className="btn-icon"
                onClick={handleCloseCamera}
              >
                <HiIcons.HiX size={20} />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '1rem' }}>
              <div className="camera-container" style={{
                position: 'relative',
                width: '100%',
                paddingBottom: '56.25%',
                backgroundColor: '#000',
                borderRadius: '8px',
                overflow: 'hidden',
                marginBottom: '1rem'
              }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="camera-video"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </div>
              <div className="camera-actions" style={{
                display: 'flex',
                gap: '0.75rem',
                alignItems: 'center',
                justifyContent: 'center',
                flexWrap: 'wrap',
                padding: '0.5rem 0'
              }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseCamera}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#6b7280',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    minWidth: '120px',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#4b5563';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#6b7280';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <HiIcons.HiX size={18} />
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={toggleCamera}
                  title={cameraFacingMode === 'environment' ? 'Usar câmera frontal' : 'Usar câmera traseira'}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#17A2B8',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    minWidth: '120px',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#138496';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#17A2B8';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <HiIcons.HiRefresh size={18} />
                  {cameraFacingMode === 'environment' ? 'Frontal' : 'Traseira'}
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-capture"
                  onClick={capturePhoto}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#10b981',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    minWidth: '140px',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#059669';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#10b981';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                  }}
                >
                  <HiIcons.HiCamera size={18} />
                  Capturar Foto
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Assinatura Digital */}
      {showSignatureModal && (
        <div className="modal-overlay" onClick={handleSignatureClose}>
          <div className="modal-content signature-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Assinatura Digital</h3>
              <button
                className="btn-icon"
                onClick={handleSignatureClose}
              >
                <HiIcons.HiX size={20} />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '1rem' }}>
              <div className="signature-container">
                <p className="signature-instruction" style={{
                  margin: '0 0 1rem 0',
                  color: '#374151',
                  fontSize: '0.875rem',
                  textAlign: 'center',
                  fontWeight: '500'
                }}>
                  Desenhe sua assinatura na área abaixo:
                </p>
                <div style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: '1.5rem'
                }}>
                  <canvas
                    ref={signatureCanvasRef}
                    className="signature-canvas"
                    width={600}
                    height={300}
                    style={{
                      width: '100%',
                      maxWidth: '600px',
                      height: '300px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      cursor: 'crosshair',
                      touchAction: 'none'
                    }}
                  />
                </div>
                <div className="signature-actions" style={{
                  display: 'flex',
                  gap: '0.75rem',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  paddingTop: '1rem',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleSignatureClear}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#6b7280',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease',
                      minWidth: '120px',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#4b5563';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#6b7280';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <HiIcons.HiX size={18} />
                    Limpar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSignatureConfirm}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#10b981',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s ease',
                      minWidth: '160px',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#059669';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#10b981';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                    }}
                  >
                    <HiIcons.HiCheck size={18} />
                    Confirmar Assinatura
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Reportar Ocorrência */}
      {showReportModal && selectedPackage && (
        <div className="modal-overlay" onClick={() => setShowReportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reportar Ocorrência - Pacote {selectedPackage.codigo_pacote || selectedPackage.codigo || selectedPackage.id}</h3>
              <button
                className="btn-icon"
                onClick={() => {
                  setShowReportModal(false);
                  setSelectedPackage(null);
                  setReportData({ tipo: '', descricao: '', fotos: [] });
                }}
              >
                <HiIcons.HiX size={20} />
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => { e.preventDefault(); handleSubmitReport(); }}>
                <div className="form-group">
                  <label>Tipo de Ocorrência *</label>
                  <Select
                    value={reportData.tipo}
                    onChange={(e) => setReportData(prev => ({ ...prev, tipo: e.target.value }))}
                    required
                    options={[
                      { value: '', label: 'Selecione...' },
                      { value: 'chegou', label: 'Chegou' },
                      { value: 'extraviado', label: 'Extraviado' },
                      { value: 'violado', label: 'Violado' }
                    ]}
                  />
                </div>
                <div className="form-group">
                  <label>Descrição (opcional):</label>
                  <textarea
                    value={reportData.descricao}
                    onChange={(e) => setReportData(prev => ({ ...prev, descricao: e.target.value }))}
                    placeholder="Descreva a ocorrência..."
                    rows="4"
                  />
                </div>
                <div className="form-group">
                  <label>Fotos (opcional):</label>
                  <div className="photo-options" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'nowrap', marginBottom: '12px' }}>
                    <button
                      type="button"
                      className="btn-camera"
                      onClick={() => {
                        setCameraField('report_foto');
                        setShowCameraModal(true);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 20px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <HiIcons.HiCamera size={20} />
                      Tirar Foto
                    </button>
                    <span style={{ color: '#666', whiteSpace: 'nowrap' }}>ou</span>
                    <label
                      className="btn-select-file"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 20px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <HiIcons.HiCloudUpload size={20} />
                      Selecionar no Dispositivo
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={async (e) => {
                          const files = Array.from(e.target.files || []);
                          const compressedFiles = await Promise.all(
                            files.map(async (file) => await compressImage(file))
                          );
                          setReportData(prev => ({ ...prev, fotos: [...(prev.fotos || []), ...compressedFiles] }));
                        }}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                  {reportData.fotos && reportData.fotos.length > 0 && (
                    <div className="photo-preview-list" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
                      {reportData.fotos.map((foto, idx) => (
                        <div key={idx} style={{ position: 'relative', width: '100px', height: '100px' }}>
                          <img
                            src={foto instanceof File ? URL.createObjectURL(foto) : foto}
                            alt={`Foto ${idx + 1}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setReportData(prev => ({
                                ...prev,
                                fotos: prev.fotos.filter((_, i) => i !== idx)
                              }));
                            }}
                            style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              background: 'rgba(0,0,0,0.6)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer'
                            }}
                          >
                            <HiIcons.HiX size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={reportingPackage}
                >
                  {reportingPackage ? 'Reportando...' : 'Reportar Ocorrência'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Ver Reportes */}
      {showViewReportsModal && selectedPackageForReports && (
        <div className="modal-overlay" onClick={() => setShowViewReportsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3>Reportes - Pacote {selectedPackageForReports.codigo_pacote || selectedPackageForReports.codigo || selectedPackageForReports.id}</h3>
              <button
                className="btn-icon"
                onClick={() => {
                  setShowViewReportsModal(false);
                  setSelectedPackageForReports(null);
                }}
              >
                <HiIcons.HiX size={20} />
              </button>
            </div>
            <div className="modal-body">
              {selectedPackageForReports.reportes && Array.isArray(selectedPackageForReports.reportes) && selectedPackageForReports.reportes.length > 0 ? (
                <div className="reports-list">
                  {selectedPackageForReports.reportes.map((reporte, index) => {
                    const tipoMap = {
                      'chegou': 'Chegou',
                      'extraviado': 'Extraviado',
                      'violado': 'Violado'
                    };
                    const tipoLabel = tipoMap[reporte.tipo] || reporte.tipo;
                    const tipoColor = {
                      'chegou': '#10b981',
                      'extraviado': '#ef4444',
                      'violado': '#f59e0b'
                    }[reporte.tipo] || '#6b7280';

                    return (
                      <div key={reporte.id || index} className="reporte-item" style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '16px',
                        backgroundColor: '#f9fafb'
                      }}>
                        <div className="reporte-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span
                              style={{
                                padding: '4px 12px',
                                borderRadius: '6px',
                                backgroundColor: `${tipoColor}20`,
                                color: tipoColor,
                                fontSize: '14px',
                                fontWeight: '600'
                              }}
                            >
                              {tipoLabel}
                            </span>
                            {reporte.created_at && (
                              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                {new Date(reporte.created_at).toLocaleString('pt-BR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            )}
                          </div>
                        </div>
                        {reporte.descricao && (
                          <div className="reporte-descricao" style={{ marginBottom: '12px' }}>
                            <strong>Descrição:</strong>
                            <p style={{ marginTop: '4px', color: '#374151' }}>{reporte.descricao}</p>
                          </div>
                        )}
                        {reporte.fotos && Array.isArray(reporte.fotos) && reporte.fotos.length > 0 && (
                          <div className="reporte-fotos" style={{ marginTop: '12px' }}>
                            <strong>Fotos:</strong>
                            <div className="photos-grid" style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                              gap: '8px',
                              marginTop: '8px'
                            }}>
                              {reporte.fotos.map((foto, fotoIndex) => {
                                const rawFotoUrl = typeof foto === 'string' ? foto : (foto?.url || foto?.preview);
                                if (!rawFotoUrl) return null;
                                const fotoUrl = normalizeImageUrl(rawFotoUrl);

                                return (
                                  <div key={fotoIndex} className="photo-item" style={{ position: 'relative' }}>
                                    <img
                                      src={fotoUrl}
                                      alt={`Foto ${fotoIndex + 1} do reporte`}
                                      onClick={() => window.open(fotoUrl, '_blank')}
                                      style={{
                                        cursor: 'pointer',
                                        width: '100%',
                                        height: '100px',
                                        objectFit: 'cover',
                                        borderRadius: '6px',
                                        border: '1px solid #e5e7eb'
                                      }}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        <div className="reporte-info" style={{ marginTop: '12px', fontSize: '12px', color: '#6b7280' }}>
                          {reporte.numero_documento && (
                            <span>Documento: {reporte.numero_documento}</span>
                          )}
                          {reporte.codigo_acesso && (
                            <span style={{ marginLeft: '12px' }}>Código: {reporte.codigo_acesso}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  <HiIcons.HiInformationCircle size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                  <p>Nenhum reporte registrado para este pacote.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="notifications-container">
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default Verify;

